import type { ComputedRef, Ref } from '@vue/composition-api';
import { computed, watch, ref } from '@vue/composition-api';
import { defineStore, storeToRefs } from 'pinia';
import { useLaunchDarkly } from '@getaccept/lib-shared/src/launch-darkly/launch-darkly.composable';
import {
  ModuleName,
  ModuleStatus,
} from '@getaccept/lib-shared/src/entity/app-modules/enums/app-module';
import { useEntityStore } from '../../entity/entity.store';
import { useUserStore } from '../../user/store/user.store';

export const useModulesStore = defineStore('modules', () => {
  const { entity, entityId } = storeToRefs(useEntityStore());
  const { flags } = useLaunchDarkly();
  const { user } = storeToRefs(useUserStore());

  const activeModule: Ref<ModuleName> = ref(null);

  const localStorageKey = computed(() => `GA_ActiveModule_${entityId.value}`);

  const allowedModules: ComputedRef<ModuleName[]> = computed(() => {
    const hasContractsRoom = flags.value.newSubscription
      ? user.value?.userProfile.access?.contractroomAccess
      : entity.value?.modules?.some(module => module.name === ModuleName.Contracts);

    const hasDealRoom = flags.value.newSubscription
      ? user.value?.userProfile.access?.dealroomAccess
      : entity.value?.modules?.find(module => module.name === ModuleName.DealRooms)?.status ===
        ModuleStatus.Active;

    const modules = [];

    if (hasContractsRoom) {
      modules.push(ModuleName.Contracts);
    }

    if (hasDealRoom) {
      modules.push(ModuleName.DealRooms);
    }

    return modules;
  });

  const isInvalidModuleAccess = computed(() => {
    if (!user.value || !flags.value.newSubscription) {
      return false;
    }

    const { contractroomAccess, dealroomAccess } = user.value.userProfile.access;
    return contractroomAccess === false && dealroomAccess === false;
  });

  const hasContractRoomModuleAccess = computed(() =>
    allowedModules.value.includes(ModuleName.Contracts)
  );

  const hasDealRoomModuleAccess = computed(() =>
    allowedModules.value.includes(ModuleName.DealRooms)
  );

  const hasInactiveDealRoomModule = computed(
    () =>
      entity.value?.modules?.find(module => module.name === ModuleName.DealRooms)?.status ===
      ModuleStatus.Inactive
  );

  const isDealRoomModuleActive = computed(() => activeModule.value === ModuleName.DealRooms);
  const isContractRoomModuleActive = computed(() => activeModule.value === ModuleName.Contracts);

  const setActiveModule = (moduleName: ModuleName) => {
    const hasAccessToModule = allowedModules.value.includes(moduleName);
    activeModule.value = hasAccessToModule ? moduleName : allowedModules.value[0];

    localStorage.setItem(localStorageKey.value, activeModule.value);
  };

  watch([entityId, allowedModules], () => {
    if (!entityId.value || !allowedModules.value.length) {
      return;
    }

    const savedState = localStorage.getItem(localStorageKey.value) as ModuleName;
    setActiveModule(savedState || allowedModules.value[0]);
  });

  return {
    activeModule,
    setActiveModule,
    hasContractRoomModuleAccess,
    hasDealRoomModuleAccess,
    hasInactiveDealRoomModule,
    isContractRoomModuleActive,
    isDealRoomModuleActive,
    allowedModules,
    isInvalidModuleAccess,
  };
});
